import React, {
    ReactElement,
    useState,
} from "react";
import { Form } from "react-bootstrap";
import { GetChannelTagsDocument, GetSettingsDocument, useSaveSettingsMutation } from "../../../../../__generated__/api";
import useDirtyTracker from "../../../../../components/dirty-tracker/DirtyTracker";
import TagList from "../../../../../components/tag-list/TagList";
import WorkflowPicker from "../../../../../components/workflow/WorkflowPicker";
import useArticleContext from "../../../../../contexts/HelpContext";
import { SettingsProps } from "../../base";

export default function ChannelTagSettings(props: SettingsProps): ReactElement {
    useArticleContext("urn:fq:article:ae97d8144c784f63b7dceab397b853fa");

    const [save] = useSaveSettingsMutation();

    const [channelTags, setChannelTags] = useState(() => props.settings?.channels?.channelTags ?? []);

    const dirtyTracker = useDirtyTracker({
        finishedSavingMessage: "Channel Tags Saved!",
        onSave: (event, onDone) => {
            event.preventDefault();
            (async () => {
                await save({
                    refetchQueries: [
                        { query: GetChannelTagsDocument },
                        { query: GetSettingsDocument },
                    ],
                    variables: {
                        channels: {
                            channelTags,
                        },
                    },
                });
                onDone();
            })();
        },
    });

    dirtyTracker.useWatchProperty(channelTags?.join(), (props.settings?.channels?.channelTags ?? []).join());

    dirtyTracker.useLoad(() => {
        setChannelTags(props.settings?.channels?.channelTags ?? []);
    }, [props.settings?.channels?.channelTags]);

    return (
        <form className="flex-grow-1">

            <div className="border p-3 rounded mb-5 bg-white">
                <h5 className="mb-3 text-light">
                    General Channel Settings
                </h5>

                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder">Channel Tags</Form.Label>
                    <TagList
                        value={channelTags as string[] ?? []}
                        onChange={value => setChannelTags(value)}
                        isTag
                        canNotHaveDuplicates
                        shouldSort
                    />
                    <Form.Text>
                        Different types of channels can be assigned tags. Tags are a way to group a set of
                        channels together for sending mass messages or using queues.
                    </Form.Text>
                </Form.Group>

            </div>

            {channelTags?.isNotEmpty && (
                <div className="border p-3 rounded mb-5 bg-white">

                    <h5 className="mb-3 text-light">
                        Automations
                    </h5>

                    <div className="list-group">
                        {channelTags?.sortBy(tag => tag).map(tag => (
                            <div
                                key={tag}
                                className={"list-group-item list-group-item-action "
                        + "d-flex align-items-center d-flex justify-content-between"}
                            >
                                <div className="flex-grow-1">
                                    <h5 className="mb-0">
                                        <code>
                                            #
                                            {tag}
                                        </code>
                                    </h5>
                                </div>
                                <div className="d-flex flex-grow-0 align-items-end">
                                    <WorkflowPicker
                                        buttonClassName="text-light"
                                        referenceId={`urn:fq:channel-tag:${tag}`}
                                        title={`Tag Automation: ${tag}`}
                                        buttonText="Automations"
                                    />
                                </div>

                            </div>

                        ))}

                    </div>

                </div>
            )}

            {dirtyTracker.saveComponent}

        </form>
    );
}
